import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function About() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d6atrenmvg9ih2c7ar5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    { 
      title: 'Roofing', 
      slug: 'roofing',
      image: 'https://public.readdy.ai/ai/img_res/84659ca88152798ed64df844e0408852.jpg'
    },
    { 
      title: 'Siding', 
      slug: 'siding',
      image: 'https://readdy.ai/api/search-image?query=Modern%20home%20exterior%20with%20elegant%20vinyl%20siding%20installation%20showing%20clean%20horizontal%20lines%20and%20professional%20finish%20in%20neutral%20gray%20tones%20architectural%20detail%20photography&width=600&height=400&seq=siding1&orientation=landscape'
    },
    { 
      title: 'Storm Restoration', 
      slug: 'storm-restoration',
      image: 'https://readdy.ai/api/search-image?query=Professional%20roof%20repair%20and%20storm%20damage%20restoration%20work%20on%20residential%20home%20showing%20workers%20installing%20new%20shingles%20after%20hail%20damage%20warm%20evening%20light&width=600&height=400&seq=storm1&orientation=landscape'
    },
    { 
      title: 'Roof Repairs', 
      slug: 'roof-repairs',
      image: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20roof%20repair%20work%20showing%20new%20shingles%20being%20installed%20on%20residential%20home%20with%20quality%20craftsmanship%20and%20attention%20to%20detail&width=600&height=400&seq=repair1&orientation=landscape'
    },
    { 
      title: 'Gutters', 
      slug: 'gutters',
      image: 'https://readdy.ai/api/search-image?query=Clean%20white%20aluminum%20gutter%20system%20installed%20on%20residential%20home%20showing%20downspout%20and%20seamless%20gutter%20with%20soffit%20detail%20professional%20installation&width=600&height=400&seq=gutter1&orientation=landscape'
    },
  ];

  return (
    <section id="about" className="bg-white relative z-10">
      {/* Services Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-3">
            {services.slice(0, 3).map((service) => (
              <Link
                key={service.slug}
                to={`/gallery/${service.slug}`}
                className="relative group cursor-pointer overflow-hidden"
              >
                <div className="w-full h-64 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2">
                    <span className="text-white text-xl font-medium">{service.title}</span>
                    <i className="ri-arrow-right-line text-white text-xl group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="grid grid-cols-2 gap-3 mt-3 max-w-4xl mx-auto">
            {services.slice(3, 5).map((service) => (
              <Link
                key={service.slug}
                to={`/gallery/${service.slug}`}
                className="relative group cursor-pointer overflow-hidden"
              >
                <div className="w-full h-64 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors" />
                  <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2">
                    <span className="text-white text-xl font-medium">{service.title}</span>
                    <i className="ri-arrow-right-line text-white text-xl group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-[#1a1a1a] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text Content */}
            <div>
              <h2 className="text-4xl font-bold text-white mb-4">Get Your Free Estimate</h2>
              <p className="text-gray-400 text-lg mb-8">
                Ready to protect your home with quality roofing and exteriors? Fill out the form and our team will get back to you within 24 hours.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#ADFF2F]/10 rounded-lg">
                    <i className="ri-phone-line text-[#ADFF2F] text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Call Us Anytime</h4>
                    <p className="text-gray-400">717-280-2005</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#ADFF2F]/10 rounded-lg">
                    <i className="ri-mail-line text-[#ADFF2F] text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email Us</h4>
                    <p className="text-gray-400">Info@ironcoreroofingandexteriors.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#ADFF2F]/10 rounded-lg">
                    <i className="ri-map-pin-line text-[#ADFF2F] text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Office Location</h4>
                    <p className="text-gray-400">2173 Embassy Dr., Lancaster, PA 17603</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="bg-[#2a2a2a] rounded-xl p-8">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#ADFF2F]/20 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-[#ADFF2F] text-3xl"></i>
                  </div>
                  <h3 className="text-white text-2xl font-bold mb-2">Thank You!</h3>
                  <p className="text-gray-400">We&apos;ll be in touch with you shortly.</p>
                </div>
              ) : (
                <form 
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ADFF2F] transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ADFF2F] transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ADFF2F] transition-colors"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-300 text-sm mb-2">Service Needed *</label>
                      <select
                        name="service"
                        required
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ADFF2F] transition-colors cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        <option value="Roofing">Roofing</option>
                        <option value="Roof Repairs">Roof Repairs</option>
                        <option value="Storm Restoration">Storm Restoration</option>
                        <option value="Siding">Siding</option>
                        <option value="Gutters">Gutters</option>
                        <option value="Free Inspection">Free Inspection</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 text-sm mb-2">Message</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ADFF2F] transition-colors resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#ADFF2F] text-black font-semibold py-4 rounded-lg hover:bg-[#9AE62A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Get Free Estimate'}
                  </button>
                  
                  {submitStatus === 'error' && (
                    <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
